<template>
  <div
    class="content-carousel"
    :style="{
      '--slides-per-page': desktopSlidesPerView,
    }"
  >
    <div :class="{ 'container': container }">
      <div
        v-if="showContentHeader"
        class="content-carousel__header"
      >
        <p
          v-if="subtitle"
          class="content-carousel__subtitle text-other-label-s text-other-label-l-desktop"
          v-text="subtitle"
        />

        <p
          v-if="title"
          class="content-carousel__title text-heading-xl text-heading-3xl-desktop"
          v-text="title"
        />

        <btn
          v-if="showCta"
          class="content-carousel__button"
          :label="ctaText"
          :url="ctaUrl"
          modifiers="text"
        />
      </div>

      <splide
        class="content-carousel__carousel"
        :has-track="false"
        :options="
          productUpsellSplideSettingsCartDrawer ?
            productUpsellSplideSettingsCarouselConfig : carouselConfig
        "
      >
        <splide-track>
          <splide-slide
            v-for="(card, index) of cards"
            :key="card.handle || card.id || card.title || index"
            class="content-carousel__slide"
          >
            <product-card
              v-if="cardType === 'product'"
              :loaded-animation="false"
              :product="card"
              :show-wishlist-icon="showWishlistIcon"
              :show-ratings="showRatings"
              :show-badges="showBadges"
              :show-quick-add="showQuickAdd"
              :search-query="searchQuery"
              sizes="(min-width: 1024px) 116px, 160px"
            />

            <content-card
              v-else-if="cardType === 'collection-quick-links'"
              v-bind="card"
              :card-type="cardType"
              :cards="cards"
              custom-element="a"
              :custom-element-url="card.collection.url"
              :image="card.collection.image"
              :title="card.collection.title"
              :loaded-animation="false"
              :sizes="
                cards?.length <= 3 ?
                  '(min-width: 1024px) 100vw, 33vw' : '(min-width: 1024px) 116px, 160px'
              "
            />

            <product-card
              v-else-if="cardType === 'product-cart-drawer'"
              card-type="product-cart-drawer"
              :loaded-animation="false"
              :product="card"
              :show-wishlist-icon="false"
              :show-ratings="false"
              :show-badges="false"
              show-quick-add
              sizes="(min-width: 1024px) 33vw, 282px"
            />

            <content-card
              v-else
              v-bind="card"
              :card-type="cardType"
              :cta-class-label="ctaClassLabel"
              :loaded-animation="false"
              sizes="(min-width: 1024px) 33vw, 282px"
            />
          </splide-slide>
        </splide-track>

        <div class="content-carousel__arrows splide__arrows">
          <button
            class="splide__arrow splide__arrow--prev"
            type="button"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.previous')"
            />

            <icon-chevron-left />
          </button>

          <button
            class="splide__arrow splide__arrow--next"
            type="button"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.next')"
            />

            <icon-chevron-right />
          </button>
        </div>

        <ul class="content-carousel__pagination splide__pagination" />
      </splide>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Content carousel (content-carousel)
 * -----------------------------------------------------------------------------
 * A carousel component to display cards.
 * - Component settings: snippet / dynamic / async / scroll
 *
 * @param {Boolean} [container] - Whether the carousel should be wrapped in a
 * container element.
 * @param {String} [title] - Section title.
 * @param {String} [subtitle] - Section subtitle.
 * @param {String} [ctaUrl] - URL for the CTA.
 * @param {String} [ctaText] - Text for the CTA.
 * @param {String} [cardType] - Card type.
 * @param {Array} [cards] - Array of cards for the carousel.
 * @param {Boolean} [carouselPagination] - Whether to display carousel
 * pagination.
 * @param {String} [ctaClassLabel] - Class for the CTA label.
 * @param {Boolean} [showWishlistIcon] - Shows wishlist icon.
 * @param {Boolean} [showRatings] - Shows ratings.
 * @param {Boolean} [showBadges] - Shows product badges.
 * @param {Boolean} [showQuickAdd] - Shows quick add button.
 * @param {Object} [productUpsellSplideSettingsCartDrawer] - Enable product
 * upsell splide settings for cart drawer.
 */
import { mapState } from 'vuex'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'

import Btn from '~global/btn/btn'
import ContentCard from '~async/content-card/content-card'
import ProductCard from '~global/product-card/product-card'

import IconChevronLeft from '~icons/directional-navigation/chevron/left.svg'
import IconChevronRight from '~icons/directional-navigation/chevron/right.svg'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'ContentCarousel',

  components: {
    Btn,
    ContentCard,
    IconChevronLeft,
    IconChevronRight,
    ProductCard,
    Splide,
    SplideSlide,
    SplideTrack,
  },

  props: {
    container: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    ctaText: {
      type: String,
      default: '',
    },
    ctaUrl: {
      type: String,
      default: '',
    },
    cardType: {
      type: String,
      default: 'regular',
    },
    cards: {
      type: Array,
      default: () => [],
    },
    carouselPagination: {
      type: Boolean,
      default: false,
    },
    ctaClassLabel: {
      type: String,
      default: '',
    },
    showWishlistIcon: {
      type: Boolean,
      default: true,
    },
    showRatings: {
      type: Boolean,
      default: true,
    },
    showBadges: {
      type: Boolean,
      default: true,
    },
    showQuickAdd: {
      type: Boolean,
      default: false,
    },
    productUpsellSplideSettingsCartDrawer: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      screenWidth: (state) => state.index.screen.width,
    }),

    /**
     * Calculate slides per view on non-mobile screen sizes.
     * – Allows for 2 cards per view if there are less than 3 cards.
     * - Does not set 1 card per row as that makes the card too wide on mobile.
     */
    desktopSlidesPerView() {
      return this.cards.length < 3 ? 2 : 3
    },

    /**
     * Calculate Splide config object.
     * - Disables dragging if there are not enough cards.
     * - Arrows and pagination are disabled with CSS in the main Splide
     *   stylesheet.
     * @returns {Object} splide config.
     */
    carouselConfig() {
      const isOverflow = this.screenWidth < breakpoints.m
        ? this.cards.length > 1
        : this.cards.length > 3

      return {
        arrows: true,
        autoWidth: true,
        drag: isOverflow,
        gap: 'var(--spacing-m)',
        mediaQuery: 'min',
        omitEnd: false,
        pagination: this.carouselPagination,
        type: 'slide',
        breakpoints: {
          [breakpoints.m]: {
            autoWidth: false,
            perPage: this.desktopSlidesPerView,
          },
        },
      }
    },

    /**
     * product upsell splide carousel config.
     * @returns {Object} splide config.
     */
    productUpsellSplideSettingsCarouselConfig() {
      const isOverflow = this.screenWidth < breakpoints.m
        ? this.cards.length > 1
        : this.cards.length > 3

      return {
        arrows: false,
        autoWidth: true,
        drag: isOverflow,
        gap: 'var(--spacing-s)',
        mediaQuery: 'min',
        omitEnd: false,
        pagination: false,
        type: 'slide',
        breakpoints: {
          [breakpoints.l]: {
            autoHeight: true,
            direction: 'ttb',
            height: '100vh',
            wheel: true,
            padding: {
              bottom: 40,
            },
          },
        },
      }
    },

    /**
     * Compute if content header should show.
     * @returns {Boolean}
     */
    showContentHeader() {
      return Boolean(this.title || this.subtitle || this.showCta)
    },

    /**
     * Compute if content header CTA should show.
     * @returns {Boolean}
     */
    showCta() {
      return Boolean(this.ctaText && this.ctaUrl)
    },
  },
}
</script>

<style lang="scss">
@import './content-carousel';
</style>
